import { Controller } from "@hotwired/stimulus"
import { destroy } from "@rails/request.js"
import flash from "../lib/flashes"
import tippy from 'tippy.js'

export default class extends Controller {
  static targets = ["count"]
  static values = {
    url: String,
    isRelated: Boolean
  }

  connect() {
    // Listen for relations:updated event to refresh when new related passwords are added
    this.relationsUpdatedListener = () => {
      this.refreshPasswords()
    }
    window.addEventListener('relations:updated', this.relationsUpdatedListener)
    
    // Initialize tooltips
    this.initializeTippy()
  }

  disconnect() {
    if (this.relationsUpdatedListener) {
      window.removeEventListener('relations:updated', this.relationsUpdatedListener)
    }
  }
  
  initializeTippy() {
    requestAnimationFrame(() => {
      if (window.initializeTippy) {
        window.initializeTippy()
      } else {
        tippy('[data-tippy-content]')
      }
    })
  }

  async confirmDelete(event) {
    event.preventDefault()
    event.stopPropagation()

    const message = 'Are you sure you want to remove this relation?' 

    if (!confirm(message)) {
      return
    }

    try {
      const response = await destroy(this.urlValue, {
        responseKind: 'turbo-stream'
      })

      if (response.ok) {
        const successMessage = "Relation removed!"
        flash(successMessage, 'success')
        
        // Find and remove the password item from the DOM
        const passwordSection = this.element.closest('section')
        if (passwordSection) {
          passwordSection.remove()
          
          // Update the count in the widget toggler
          this.updatePasswordCount()
          
          // Reinitialize tooltips
          this.initializeTippy()
        }
      } else {
        flash('Something went wrong!', 'error')
      }
    } catch (error) {
      flash('Something went wrong!', 'error')
    }
  }

  async refreshPasswords() {
    // Fetch and update the passwords section without reloading the page
    try {
      const currentUrl = window.location.href
      const response = await fetch(currentUrl, {
        headers: {
          'Accept': 'text/html'
        }
      })
      
      if (response.ok) {
        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')
        
        // Find the passwords section in the fetched HTML
        const newPasswordsSection = doc.querySelector('.asset-sidebar__group-content--Passwords')
        const currentPasswordsSection = document.querySelector('.asset-sidebar__group-content--Passwords')
        
        if (newPasswordsSection && currentPasswordsSection) {
          // Replace the content
          currentPasswordsSection.innerHTML = newPasswordsSection.innerHTML
          
          // Reinitialize tooltips
          this.initializeTippy()
          
          // Update the counter in the header
          const newCount = doc.querySelector('[data-passwords-target="count"]')?.textContent

          if (newCount && this.hasCountTarget) {
            this.countTarget.textContent = newCount
          }
        }
      }
    } catch (error) {
      console.error('Error refreshing passwords:', error)
    }
  }

  updatePasswordCount() {
    // Use requestAnimationFrame to ensure DOM is updated
    requestAnimationFrame(() => {
      // Count remaining passwords in the DOM
      const passwordBox = document.querySelector('.asset-sidebar__group-content--Passwords .asset-sidebar__box')
      let remainingCount = 0
      
      if (passwordBox) {
        const remainingSections = passwordBox.querySelectorAll('section.indented')
        remainingCount = remainingSections.length
        
        // Check and hide "Child Passwords" header if no child passwords
        const allHeaders = passwordBox.querySelectorAll('header h1')
        allHeaders.forEach(header => {
          if (header.textContent === 'Child Passwords' || header.textContent === 'Related Passwords') {
            const headerElement = header.closest('header')
            const nextSections = []
            let nextElement = headerElement.nextElementSibling
            
            // Collect sections until we hit another header or end
            while (nextElement && nextElement.tagName !== 'HEADER') {
              if (nextElement.tagName === 'SECTION') {
                nextSections.push(nextElement)
              }
              nextElement = nextElement.nextElementSibling
            }
            
            // Hide header if no sections under it
            if (nextSections.length === 0) {
              headerElement.style.display = 'none'
            }
          }
        })
        
        // Show empty state if no passwords remain
        if (remainingCount === 0) {
          passwordBox.style.display = 'none'
          
          // Check if empty state exists, if not create it
          let emptyState = document.querySelector('.asset-sidebar__group-content--Passwords .asset-sidebar__empty')
          
          if (!emptyState) {
            // Create the empty state element
            const passwordsContent = document.querySelector('.asset-sidebar__group-content--Passwords')
            if (passwordsContent) {
              emptyState = document.createElement('div')
              emptyState.className = 'asset-sidebar__empty'
              emptyState.innerHTML = '<p>Attach passwords to easily access relevant credentials</p>'
              passwordsContent.appendChild(emptyState)
            }
          } else {
            emptyState.style.display = 'block'
            emptyState.style.visibility = 'visible'
          }
        }
      }
      
      // This runs on the row-level instance mounted on the delete button, so the
      // count target belongs to the group instance and has to be reached by hand.
      const countElement = this.element
        .closest('.asset-sidebar__group')
        ?.querySelector('[data-passwords-target="count"]')

      if (countElement) {
        countElement.textContent = `(${remainingCount})`
      }
    })
  }
}

